import React, { useState, useEffect, useRef } from 'react'
import {
  Box, Button, Paper, TextField, MenuItem, Stack, Typography
} from '@mui/material'
import { router, usePage } from '@inertiajs/react'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'

Form.layout = (page) => <AppShell>{page}</AppShell>

export default function Form({
  property, invoice, vendors = [], cost_categories = [], mode = 'new', errors = {}
}) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const basePath = `/m/${tenant.slug}/properties/${property.id}`
  const isNew = mode === 'new'

  const [data, setData] = useState({
    invoice_number: invoice.invoice_number || '',
    invoice_date: invoice.invoice_date || '',
    due_date: invoice.due_date || '',
    net_amount: invoice.net_amount || '0',
    vat_amount: invoice.vat_amount || '0',
    gross_amount: invoice.gross_amount || '0',
    description: invoice.description || '',
    contact_id: invoice.contact_id || '',
    cost_category_id: invoice.cost_category_id || '',
  })
  const [file, setFile] = useState(null)
  const [grossOverridden, setGrossOverridden] = useState(false)
  const fileInputRef = useRef(null)

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }))

  // Auto-compute gross = net + vat unless user overrode it
  useEffect(() => {
    if (grossOverridden) return
    const net = parseFloat(data.net_amount) || 0
    const vat = parseFloat(data.vat_amount) || 0
    const gross = (net + vat).toFixed(2)
    setData((d) => ({ ...d, gross_amount: gross }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.net_amount, data.vat_amount])

  const handleSubmit = (e) => {
    e.preventDefault()
    const payload = new FormData()
    Object.entries(data).forEach(([k, v]) => payload.append(`invoice[${k}]`, v ?? ''))
    if (file) payload.append('invoice[file]', file)

    if (isNew) {
      router.post(`${basePath}/invoices`, payload, { forceFormData: true })
    } else {
      payload.append('_method', 'patch')
      router.post(`${basePath}/invoices/${invoice.id}`, payload, { forceFormData: true })
    }
  }

  const errText = (key) => {
    const e = errors[key]
    if (!e) return undefined
    return Array.isArray(e) ? e.join(', ') : String(e)
  }

  return (
    <Box>
      <PageHeader
        title={isNew ? 'Neue Rechnung' : `Rechnung ${invoice.invoice_number || ''} bearbeiten`}
        breadcrumbs={[
          { label: property.name, href: basePath },
          { label: 'Rechnungen', href: `${basePath}/invoices` },
          { label: isNew ? 'Neu' : 'Bearbeiten' },
        ]}
      />

      <Paper component="form" onSubmit={handleSubmit} elevation={0}
        sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Rechnungsnummer" fullWidth required
              value={data.invoice_number} onChange={(e) => set('invoice_number', e.target.value)}
              error={!!errText('invoice_number')} helperText={errText('invoice_number')}
              slotProps={{ htmlInput: { 'data-testid': 'invoice-number' } }}
            />
            <TextField
              select label="Lieferant" fullWidth required
              value={data.contact_id} onChange={(e) => set('contact_id', e.target.value)}
              error={!!errText('contact_id')} helperText={errText('contact_id')}
              data-testid="contact-id"
            >
              <MenuItem value="">—</MenuItem>
              {vendors.map((v) => (
                <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              type="date" label="Rechnungsdatum" fullWidth required
              value={data.invoice_date} onChange={(e) => set('invoice_date', e.target.value)}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'invoice-date' } }}
              error={!!errText('invoice_date')} helperText={errText('invoice_date')}
            />
            <TextField
              type="date" label="Fällig am" fullWidth required
              value={data.due_date} onChange={(e) => set('due_date', e.target.value)}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'due-date' } }}
              error={!!errText('due_date')} helperText={errText('due_date')}
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              type="number" label="Netto" fullWidth required
              value={data.net_amount} onChange={(e) => set('net_amount', e.target.value)}
              slotProps={{ htmlInput: { step: '0.01', 'data-testid': 'net-amount' } }}
              error={!!errText('net_amount')} helperText={errText('net_amount')}
            />
            <TextField
              type="number" label="USt" fullWidth
              value={data.vat_amount} onChange={(e) => set('vat_amount', e.target.value)}
              slotProps={{ htmlInput: { step: '0.01', 'data-testid': 'vat-amount' } }}
              error={!!errText('vat_amount')} helperText={errText('vat_amount')}
            />
            <TextField
              type="number" label="Brutto" fullWidth required
              value={data.gross_amount}
              onChange={(e) => { setGrossOverridden(true); set('gross_amount', e.target.value) }}
              slotProps={{ htmlInput: { step: '0.01', 'data-testid': 'gross-amount' } }}
              error={!!errText('gross_amount')} helperText={errText('gross_amount')}
            />
          </Stack>

          <TextField
            select label="Kostenart" fullWidth
            value={data.cost_category_id} onChange={(e) => set('cost_category_id', e.target.value)}
            data-testid="cost-category-id"
          >
            <MenuItem value="">—</MenuItem>
            {cost_categories.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Beschreibung" fullWidth multiline minRows={2}
            value={data.description} onChange={(e) => set('description', e.target.value)}
          />

          {isNew && (
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Beleg (PDF, ZUGFeRD oder X-Rechnung)
              </Typography>
              <input type="file"
                ref={fileInputRef}
                data-testid="invoice-file"
                accept="application/pdf,application/xml,text/xml"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              {file && (
                <Typography variant="caption" sx={{ ml: 2 }}>{file.name}</Typography>
              )}
            </Box>
          )}

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="outlined"
              onClick={() => router.visit(`${basePath}/invoices`)}>
              Abbrechen
            </Button>
            <Button type="submit" variant="contained" data-testid="submit-form">
              {isNew ? 'Erstellen' : 'Speichern'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  )
}
